import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Smartphone, X, Upload, Link2, Phone, MessageSquareReply } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CLASS = { APPROVED: "badge-approved", PENDING: "badge-pending", REJECTED: "badge-rejected" };
const BTN_TYPES = [
  { value: "QUICK_REPLY", label: "Quick Reply", icon: MessageSquareReply },
  { value: "URL", label: "Visit Website", icon: Link2 },
  { value: "PHONE_NUMBER", label: "Call Phone", icon: Phone },
];
const EMPTY_FORM = { name: "", category: "MARKETING", language: "en", header_type: "NONE", header_text: "", body_text: "", footer_text: "", body_examples: "", header_handle: "", buttons: [] };

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [samplePreview, setSamplePreview] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadSample = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/templates/sample-upload", fd);
      setForm((f) => ({ ...f, header_handle: data.handle }));
      setSamplePreview(file.type.startsWith("image/") ? URL.createObjectURL(file) : "");
      toast.success("Sample uploaded to Meta");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setUploading(false);
    }
  };

  const setBtn = (i, patch) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) }));
  const addBtn = () => setForm((f) => ({ ...f, buttons: [...f.buttons, { type: "QUICK_REPLY", text: "", url: "", phone_number: "" }] }));
  const removeBtn = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const mediaHeader = ["IMAGE", "DOCUMENT"].includes(form.header_type);
  const needsSample = mediaHeader && !form.header_handle;
  const buttonsIncomplete = form.buttons.some((b) => !b.text.trim() || (b.type === "URL" && !b.url.trim()) || (b.type === "PHONE_NUMBER" && !b.phone_number.trim()));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/templates", {
        name: form.name, category: form.category, language: form.language,
        body_text: form.body_text,
        header_type: form.header_type === "NONE" ? null : form.header_type,
        header_text: form.header_text || null, footer_text: form.footer_text || null,
        body_examples: form.body_examples ? form.body_examples.split(",").map((s) => s.trim()) : [],
        header_handle: form.header_handle || null,
        buttons: form.buttons,
      });
      toast.success("Template submitted to Meta for approval");
      setOpen(false);
      setForm(EMPTY_FORM);
      setSamplePreview("");
      load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const bodyPreview = form.body_text.replace(/\{\{(\d+)\}\}/g, (_, n) => ["Sample", "Value2", "Value3"][n - 1] || `[${n}]`);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Meta Templates</h1>
          <div className="font-hand text-lg text-amber-800">approved templates unlock messaging beyond 24h ✦</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="refresh-templates-btn" className="btn-yellow" onClick={load}><RefreshCw size={15} /> Sync</button>
          <button data-testid="create-template-btn" className="btn-navy" onClick={() => setOpen(true)}><Plus size={15} /> New Template</button>
        </div>
      </div>

      {loading ? (
        <div className="font-hand text-xl text-amber-800">fetching from Meta...</div>
      ) : (
        <div className="card-tactile overflow-hidden">
          <table className="w-full text-sm" data-testid="templates-table">
            <thead>
              <tr className="text-left border-b-2" style={{ background: "var(--cd-yellow)", borderColor: "var(--cd-navy)" }}>
                <th className="px-4 py-2.5 font-heading">Name</th>
                <th className="px-4 py-2.5 font-heading">Category</th>
                <th className="px-4 py-2.5 font-heading">Language</th>
                <th className="px-4 py-2.5 font-heading">Status</th>
                <th className="px-4 py-2.5 font-heading">Body</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id || t.name} className="border-b hover:bg-black/[0.03]" style={{ borderColor: "#E5DCB8" }} data-testid={`template-row-${t.name}`}>
                  <td className="px-4 py-2.5 font-mono2 font-semibold">{t.name}</td>
                  <td className="px-4 py-2.5">{t.category}</td>
                  <td className="px-4 py-2.5">{t.language}</td>
                  <td className="px-4 py-2.5"><span className={`status-badge ${STATUS_CLASS[t.status] || "badge-pending"}`}>{t.status}</span></td>
                  <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md truncate">{((t.components || []).find((c) => c.type === "BODY") || {}).text}</td>
                </tr>
              ))}
              {templates.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center font-hand text-lg text-amber-800">no templates found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-tactile border-2 max-w-3xl max-h-[88vh] overflow-y-auto" style={{ borderColor: "var(--cd-navy)" }}>
          <DialogHeader><DialogTitle className="font-heading">Create Template → Submit for Meta Approval</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <label className="label-upper">Template Name (lowercase_underscore)</label>
                <input data-testid="tpl-name-input" className="input-tactile" placeholder="order_update_v1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-upper">Category</label>
                  <select data-testid="tpl-category-select" className="input-tactile" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    <option>MARKETING</option><option>UTILITY</option><option>AUTHENTICATION</option>
                  </select>
                </div>
                <div>
                  <label className="label-upper">Language</label>
                  <select data-testid="tpl-language-select" className="input-tactile" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    <option value="en">en</option><option value="en_US">en_US</option><option value="hi">hi</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label-upper">Header</label>
                <select data-testid="tpl-header-select" className="input-tactile" value={form.header_type} onChange={(e) => { setForm({ ...form, header_type: e.target.value, header_handle: "" }); setSamplePreview(""); }}>
                  <option value="NONE">None</option><option value="TEXT">Text</option><option value="IMAGE">Image</option><option value="DOCUMENT">Document</option>
                </select>
              </div>
              {form.header_type === "TEXT" && (
                <input data-testid="tpl-header-text-input" className="input-tactile" placeholder="Header text" value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} />
              )}
              {mediaHeader && (
                <div>
                  <label className="label-upper">Sample {form.header_type.toLowerCase()} (required by Meta for approval)</label>
                  <label className={`flex items-center gap-2 input-tactile cursor-pointer text-sm ${form.header_handle ? "text-green-700 font-semibold" : "text-slate-500"}`} data-testid="tpl-sample-upload">
                    <Upload size={15} />
                    {uploading ? "Uploading to Meta..." : form.header_handle ? "Sample uploaded ✓ — click to replace" : `Upload sample ${form.header_type.toLowerCase()}`}
                    <input type="file" className="hidden" accept={form.header_type === "IMAGE" ? "image/jpeg,image/png" : "application/pdf"} onChange={(e) => uploadSample(e.target.files[0])} />
                  </label>
                </div>
              )}
              <div>
                <label className="label-upper">Body (use {"{{1}}"}, {"{{2}}"} for variables)</label>
                <textarea data-testid="tpl-body-input" className="input-tactile" rows={4} placeholder="Hi {{1}}, your order {{2}} has shipped!" value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} />
              </div>
              {/\{\{\d+\}\}/.test(form.body_text) && (
                <div>
                  <label className="label-upper">Example values (comma separated, required by Meta)</label>
                  <input data-testid="tpl-examples-input" className="input-tactile" placeholder="Rahul, #4521" value={form.body_examples} onChange={(e) => setForm({ ...form, body_examples: e.target.value })} />
                </div>
              )}
              <div>
                <label className="label-upper">Footer (optional)</label>
                <input data-testid="tpl-footer-input" className="input-tactile" placeholder="StarAndDaisy" value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="label-upper">Buttons ({form.buttons.length}/3)</label>
                  {form.buttons.length < 3 && (
                    <button data-testid="tpl-add-button-btn" className="btn-ghost2 text-xs font-bold" onClick={addBtn}><Plus size={13} /> Add button</button>
                  )}
                </div>
                {form.buttons.map((b, i) => (
                  <div key={i} className="border-2 rounded-lg p-2 mt-1.5 space-y-1.5 bg-white/60" style={{ borderColor: "var(--cd-navy)" }} data-testid={`tpl-button-row-${i}`}>
                    <div className="flex gap-1.5">
                      <select className="input-tactile py-1 text-xs flex-1" value={b.type} onChange={(e) => setBtn(i, { type: e.target.value })} data-testid={`tpl-button-type-${i}`}>
                        {BTN_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                      <input className="input-tactile py-1 text-xs flex-1" placeholder="Button text (max 25)" maxLength={25} value={b.text} onChange={(e) => setBtn(i, { text: e.target.value })} data-testid={`tpl-button-text-${i}`} />
                      <button className="btn-ghost2 px-1.5" onClick={() => removeBtn(i)} data-testid={`tpl-button-remove-${i}`}><X size={14} /></button>
                    </div>
                    {b.type === "URL" && (
                      <input className="input-tactile py-1 text-xs" placeholder="https://staranddaisy.in/track" value={b.url} onChange={(e) => setBtn(i, { url: e.target.value })} data-testid={`tpl-button-url-${i}`} />
                    )}
                    {b.type === "PHONE_NUMBER" && (
                      <input className="input-tactile py-1 text-xs" placeholder="+919355547971" value={b.phone_number} onChange={(e) => setBtn(i, { phone_number: e.target.value })} data-testid={`tpl-button-phone-${i}`} />
                    )}
                  </div>
                ))}
              </div>
              <button data-testid="tpl-submit-btn" className="btn-navy w-full justify-center" disabled={busy || uploading || !form.name || !form.body_text || needsSample || buttonsIncomplete} onClick={submit}>
                {busy ? "Submitting..." : needsSample ? "Upload a sample first" : "Submit for Approval"}
              </button>
            </div>
            <div>
              <div className="label-upper flex items-center gap-1"><Smartphone size={12} /> Live Preview</div>
              <div className="rounded-[2rem] border-4 p-3 mx-auto w-64 chat-bg" style={{ borderColor: "var(--cd-navy)" }} data-testid="tpl-live-preview">
                <div className="bubble-in px-3 py-2 text-xs space-y-1.5">
                  {form.header_type === "TEXT" && form.header_text && <div className="font-bold">{form.header_text}</div>}
                  {form.header_type === "IMAGE" && (samplePreview
                    ? <img src={samplePreview} alt="header" className="rounded-lg w-full h-24 object-cover border" />
                    : <div className="h-20 rounded-lg flex items-center justify-center text-slate-500 bg-slate-100 border">🖼 image header</div>)}
                  {form.header_type === "DOCUMENT" && <div className="h-10 rounded-lg flex items-center justify-center text-slate-500 bg-slate-100 border">📄 {form.header_handle ? "sample.pdf ✓" : "document"}</div>}
                  <div className="whitespace-pre-wrap">{bodyPreview || "Your message body appears here..."}</div>
                  {form.footer_text && <div className="text-[10px] text-slate-500">{form.footer_text}</div>}
                  <div className="text-[9px] text-slate-400 text-right">10:24 AM</div>
                </div>
                {form.buttons.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {form.buttons.map((b, i) => {
                      const Icon = (BTN_TYPES.find((t) => t.value === b.type) || BTN_TYPES[0]).icon;
                      return (
                        <div key={i} className="bg-white rounded-lg border text-center py-1.5 text-[11px] font-semibold text-sky-600 flex items-center justify-center gap-1" style={{ borderColor: "#d5cdb0" }}>
                          <Icon size={11} /> {b.text || "Button"}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
